/**
 * Die Hülle: was auf jedem Bildschirm gleich bleibt — wo man steht, wohin man kann und wie man
 * wieder herauskommt.
 *
 * Diese Datei ist nachgereicht, und der Grund gehört hierher: der Vertrag zu Teil 4 zählte
 * sechzehn Bildschirme auf und keine Hülle. Gebaut wurde daraufhin eine Anwendung, in der
 * `MandantenWechsler` und `Abmelden` geschrieben, geprüft und an keiner Stelle eingebunden waren.
 * Wer sich anmeldete, landete auf einer nackten Überschrift und erreichte die anderen fünfzehn
 * Bildschirme nur durch Eintippen von Adressen; abmelden konnte er sich nirgends. Eine Sammlung
 * von Bildschirmen ist keine Anwendung.
 *
 * Gerechnet wird hier nichts. Die Hülle liest die Sitzung — dieselbe eine Abfrage, die auch der
 * Zugangsschutz liest, aus demselben Cache — und zeigt an, was darin steht.
 */
import { useNavigate, useRouterState } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { Abmelden, MandantenWechsler, hatMehrereBereiche, useSession } from '../auth'
import { FarbschemaSchalter } from './FarbschemaSchalter'
import { Icon, type IconName } from './Icon'
import { Verweis } from './Verweis'

/** Ein Navigationseintrag: die Adresse, das Wort, das auch als Überschrift auf ihr steht, und sein
 *  Icon in der Seitenleiste. */
export interface NaviEintrag {
  pfad: string
  text: string
  icon: IconName
}

/**
 * Die neun Bildschirme des Auftraggebers, in der Reihenfolge des Arbeitstages: erst der Überblick,
 * dann was zu klären ist, dann die Belege, zuletzt die Einrichtung.
 */
export function tenantNavigation(tenantId: string): NaviEintrag[] {
  const t = `/t/${encodeURIComponent(tenantId)}`
  return [
    { pfad: `${t}/dashboard`, text: 'Dashboard', icon: 'dashboard' },
    { pfad: `${t}/pruefen`, text: 'Prüfen', icon: 'pruefen' },
    { pfad: `${t}/abschluesse`, text: 'Abschlüsse', icon: 'abschluesse' },
    { pfad: `${t}/provisionen`, text: 'Provisionen', icon: 'provisionen' },
    { pfad: `${t}/rechnungen`, text: 'Rechnungen', icon: 'rechnungen' },
    { pfad: `${t}/zahlungen`, text: 'Zahlungen', icon: 'zahlungen' },
    { pfad: `${t}/regeln`, text: 'Produkte & Regeln', icon: 'regeln' },
    { pfad: `${t}/exporte`, text: 'Exporte', icon: 'exporte' },
    { pfad: `${t}/einstellungen`, text: 'Einstellungen', icon: 'einstellungen' },
  ]
}

/** Die fünf Bildschirme des Auftragnehmers. */
export const ME_NAVIGATION: readonly NaviEintrag[] = [
  { pfad: '/me/provisionen', text: 'Meine Provisionen', icon: 'geldboerse' },
  { pfad: '/me/pipeline', text: 'Meine Pipeline', icon: 'pipeline' },
  { pfad: '/me/abrechnen', text: 'Abrechnen', icon: 'abrechnen' },
  { pfad: '/me/rechnungen', text: 'Meine Abrechnungen', icon: 'rechnungen' },
  { pfad: '/me/profil', text: 'Mein Profil', icon: 'profil' },
]

/** Die beiden Bildschirme der Plattform. Sichtbar nur für `user.is_platform_admin`. */
export const ADMIN_NAVIGATION: readonly NaviEintrag[] = [
  { pfad: '/admin/mandanten', text: 'Auftraggeber', icon: 'mandanten' },
  { pfad: '/admin/system', text: 'System', icon: 'system' },
]

/**
 * Der Bereich, in dem der aktuelle Pfad liegt. Der Pfad ist die Quelle, nicht die Sitzung: er ist
 * das, was der Nutzer sieht, und er beantwortet die Frage auch dann, wenn die Sitzung mehrere
 * Bereiche führt.
 */
export type Bereich =
  | { art: 'tenant'; tenantId: string }
  | { art: 'me' }
  | { art: 'admin' }
  | { art: 'kein' }

export function bereichAusPfad(pfad: string): Bereich {
  const treffer = /^\/t\/([^/]+)/.exec(pfad)
  if (treffer !== null) {
    // Der Pfad kommt kodiert aus dem Browser; die Sitzung und `tenantNavigation` arbeiten mit dem
    // rohen Wert. Ein unvollständiges Prozentzeichen in einer von Hand getippten Adresse darf die
    // Kopfzeile nicht abstürzen lassen — dann bleibt der Wert eben, wie er ist.
    let roh = treffer[1] as string
    try {
      roh = decodeURIComponent(roh)
    } catch {
      /* unverändert weiterreichen */
    }
    return { art: 'tenant', tenantId: roh }
  }
  if (pfad === '/me' || pfad.startsWith('/me/')) return { art: 'me' }
  if (pfad === '/admin' || pfad.startsWith('/admin/')) return { art: 'admin' }
  return { art: 'kein' }
}

/** Zwei Buchstaben für den runden Platzhalter neben dem Namen — aus dem Namen, sonst aus der Adresse. */
export function initialen(name: string, email: string): string {
  const woerter = name.trim().split(/\s+/).filter((wort) => wort.length > 0)
  if (woerter.length >= 2) return `${woerter[0]![0]}${woerter[woerter.length - 1]![0]}`.toUpperCase()
  if (woerter.length === 1) return woerter[0]!.slice(0, 2).toUpperCase()
  return email.slice(0, 2).toUpperCase()
}

/**
 * Die Seitenleiste neben jedem Bildschirm — gebaut wie die des FinSuites-CRM: oben die Marke,
 * darunter die Karte des Bereichs (mit Wechsler, wenn es mehrere gibt), dann die Wege, unten wer
 * angemeldet ist. Ohne geladene Sitzung erscheint sie nicht: auf der Anmeldung und auf der
 * Einladungsseite gibt es weder einen Bereich zu wechseln noch etwas abzumelden.
 */
export function Kopfzeile(): ReactElement | null {
  const sitzung = useSession()
  const pfad = useRouterState({ select: (zustand) => zustand.location.pathname })
  const navigate = useNavigate()
  const gehZu = (ziel: string): void => {
    void navigate({ to: ziel })
  }

  if (!sitzung.isSuccess) return null

  const bereich = bereichAusPfad(pfad)
  const eintraege: readonly NaviEintrag[] =
    bereich.art === 'tenant'
      ? tenantNavigation(bereich.tenantId)
      : bereich.art === 'me'
        ? ME_NAVIGATION
        : bereich.art === 'admin'
          ? ADMIN_NAVIGATION
          : []

  // Der Wechsler blendet sich selbst aus, wenn es nur einen Bereich gibt. Dann muss der Name des
  // Bereichs trotzdem dastehen — sonst weiß niemand, wessen Zahlen er gerade liest.
  const mehrere = hatMehrereBereiche(sitzung.data)
  const tenantName =
    bereich.art === 'tenant'
      ? (sitzung.data.tenants.find((eintrag) => eintrag.id === bereich.tenantId)?.name ?? null)
      : null
  const bereichsName =
    bereich.art === 'tenant'
      ? tenantName
      : bereich.art === 'me'
        ? 'Meine Provisionen'
        : bereich.art === 'admin'
          ? 'Plattform'
          : null
  const rolle =
    bereich.art === 'tenant' ? 'Auftraggeber' : bereich.art === 'me' ? 'Auftragnehmer' : 'Verwaltung'
  const { user } = sitzung.data

  return (
    <header className="seitenleiste">
      <div className="leiste-kopf">
        <Verweis href="/" gehZu={gehZu} className="leiste-marke">
          <img
            className="leiste-wortmarke"
            src="/brand/finsuites-wortmarke.png"
            alt="FinSuites"
            width={456}
            height={89}
          />
          <span className="leiste-produkt">Accounting</span>
        </Verweis>
      </div>

      {mehrere ? (
        <div className="leiste-bereich leiste-bereich-wahl">
          <Icon name="ort" className="leiste-bereich-icon" />
          <MandantenWechsler
            aktuellerTenantId={bereich.art === 'tenant' ? bereich.tenantId : undefined}
          />
        </div>
      ) : (
        bereichsName !== null && (
          <div className="leiste-bereich">
            <Icon name="ort" className="leiste-bereich-icon" />
            <span className="leiste-bereich-text">
              <span className="leiste-bereich-name">{bereichsName}</span>
              <span className="leiste-bereich-rolle">{rolle}</span>
            </span>
          </div>
        )
      )}

      {eintraege.length > 0 && (
        <nav aria-label="Bereich" className="hauptnavigation">
          <ul>
            {eintraege.map((eintrag) => (
              <li key={eintrag.pfad}>
                <Verweis
                  href={eintrag.pfad}
                  gehZu={gehZu}
                  // `aria-current="page"` und nicht nur eine Farbe: der Punkt, auf dem man steht,
                  // muss auch ansagbar sein, nicht bloß sichtbar (WCAG 1.4.1).
                  aktuell={pfad === eintrag.pfad}
                >
                  <Icon name={eintrag.icon} />
                  <span>{eintrag.text}</span>
                </Verweis>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="leiste-fuss">
        {user.is_platform_admin && bereich.art !== 'admin' && (
          <Verweis href="/admin/mandanten" gehZu={gehZu} className="leiste-plattform">
            <Icon name="plattform" />
            <span>Plattform</span>
          </Verweis>
        )}
        <div className="leiste-nutzer">
          <span className="leiste-avatar" aria-hidden="true">
            {initialen(user.display_name, user.email)}
          </span>
          <span className="leiste-nutzer-text">
            <span className="leiste-nutzer-name">{user.display_name}</span>
            <span className="leiste-nutzer-mail">{user.email}</span>
          </span>
        </div>
        <div className="leiste-abmelden">
          <Abmelden />
        </div>
      </div>
    </header>
  )
}

/**
 * Die Fußzeile. Der Farbschemaschalter steht hier und nicht oben: die Kopfzeile gehört dem
 * Wechseln und dem Abmelden, und ein Farbschema wählt man einmal, nicht täglich. Ganz am Ende der
 * Tabulatorreihenfolge steht er außerdem keinem der Geldabläufe im Weg.
 */
export function Fusszeile(): ReactElement {
  return (
    <footer className="huelle-fuss">
      <FarbschemaSchalter />
    </footer>
  )
}
